import React, { useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "./styles.css";

const BarChart = ({
  data = {},
  options = {},
  enableSorting = false,
  enableDualAxis = false,
  yAxisTitle = "",
}) => {
  const chartComponentRef = useRef(null);

  const labels = data?.labels || [];
  const datasets = data?.datasets || [];

  let finalLabels = [...labels];
  let finalDataSets = datasets.map((ds) => ({ ...ds }));

  if (enableSorting && datasets.length > 1 && datasets[1].data) {
    const combined = labels.map((label, idx) => ({
      label,
      values: datasets.map((ds) => ds.data[idx]),
    }));

    combined.sort((a, b) => b.values[1] - a.values[1]);

    finalLabels = combined.map((c) => c.label);
    finalDataSets = datasets.map((ds, i) => ({
      ...ds,
      data: combined.map((c) => c.values[i]),
    }));
  }

  const series = finalDataSets.map((dataset, index) => ({
    name: dataset?.label || `Series ${index + 1}`,
    data:
      dataset?.data?.map((value, i) => ({
        y: value,
        max_date: dataset?.max_date?.[i] || null,
        min_date: dataset?.min_date?.[i] || null,
      })) || [],
    color: {
      linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
      stops: [
        [0, dataset?.gradientFrom || "#378894ff"],
        [1, dataset?.gradientTo || "#1c383c"],
      ],
    },
    yAxis: enableDualAxis ? index : 0,
    borderRadius: 3,
  }));
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const chartOptions = {
    accessibility: {
      enabled: false,
    },
    chart: {
      type: "column",
      backgroundColor: "transparent",
    },
    title: {
      text: options?.title || "",
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#333",
      },
    },
    xAxis: {
      categories: finalLabels.map((lbl) => lbl.replace(".", " ")),
      crosshair: true,
      labels: {
        rotation: -40,
        style: { font: "Inter, sans-serif", fontSize: "12px", color: "#444" },
      },
    },
    yAxis: enableDualAxis
      ? [
          {
            title: {
              text: finalDataSets[0]?.label || "Y-Axis 1",
              style: {
                color: series[0]?.color || "#0033a0",
                fontWeight: "600",
              },
            },
            labels: { style: { color: series[0]?.color || "#0033a0" } },
            opposite: false,
          },
          {
            title: {
              text: finalDataSets[1]?.label || "Y-Axis 2",
              style: {
                color: series[1]?.color || "#FF5733",
                fontWeight: "600",
              },
            },
            labels: { style: { color: series[1]?.color || "#FF5733" } },
            opposite: true,
          },
        ]
      : [
          {
            title: {
              text: yAxisTitle
                ? yAxisTitle || "Y-Axis"
                : finalDataSets[0]?.label || "Y-Axis",
              style: {
                color: series[0]?.color || "#0033a0",
                fontWeight: "600",
              },
            },
            labels: { style: { color: series[0]?.color || "#0033a0" } },
          },
        ],
    tooltip: {
      shared: true,
      backgroundColor: "#fff",
      borderColor: "#ddd",
      borderRadius: 8,
      shadow: false,
      useHTML: true,
      formatter: function () {
        let tooltipHtml = `<div style="font-family:Inter, sans-serif; font-size:13px;">`;
        this.points?.forEach((p) => {
          const maxDate = p.point.max_date
            ? `<br/>(Max Date: ${formatDate(p.point.max_date)})`
            : "";
          const minDate = p.point.min_date
            ? `<br/>(Min Date: ${formatDate(p.point.min_date)})`
            : "";

          tooltipHtml += `
        <span style="color:${p.color}">●</span>
        <b>${p.series.name}</b>: <b>${Math.round(p.y)}</b>
        ${maxDate}${minDate}<br/>
      `;
        });
        tooltipHtml += "</div>";
        return tooltipHtml;
      },
    },

    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: { fontSize: "13px", color: "#333" },
      symbolRadius: 3,
    },
    plotOptions: {
      column: {
        borderRadius: 3,
        grouping: true,
        groupPadding: 0.1,
        pointPadding: 0.05,
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          inside: true,
          align: "center",
          verticalAlign: "middle",
          style: {
            fontSize: "12px",
            fontWeight: "600",
            color: "#ffffff",
            textOutline: "none",
          },
          formatter: function () {
            return Math.round(this.y);
          },
        },
      },
    },
    series,
    credits: { enabled: false },
  };

  return (
    <div className="bar-chart" style={{ height: "100%", width: "100%" }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={chartComponentRef}
        containerProps={{ style: { width: "100%", height: "100%" } }}
      />
    </div>
  );
};

export default React.memo(BarChart);
